import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLocation, useNavigate } from "react-router";
import { Images } from "../controller/Images";
import { GlobalStateContext } from "../controller/GlobalState";
import {
  emailRegex, isValidPhone, tech, workflowsteps, chooseus, aiServicePages,
} from "../controller/global";
import { submitLead } from "../controller/leads";

const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

const CATEGORY_NUM_LABELS = {
  "front-end": "01 / FRONT-END",
  "mobile-app": "02 / MOBILE",
  "back-end": "03 / BACK-END",
  "database": "04 / DATABASE",
  "devops": "05 / DEVOPS & CLOUD",
  "design": "06 / DESIGN",
};

const totalTools = tech.reduce((sum, cat) => sum + cat.children.length, 0);

const QUICK_FACTS = [
  { value: String(tech.length), label: "Technology Categories" },
  { value: `${totalTools}+`, label: "Frameworks & Tools" },
  { value: String(workflowsteps.length), label: "Stage Delivery Process" },
  { value: String(aiServicePages.length), label: "Dedicated AI Services" },
];

const AI_CAPABILITIES = [
  {
    num: "01 / AUTONOMY",
    title: "Agentic AI Development",
    copy: "Autonomous agent systems built on the same React, Node.js, and .NET foundations listed on this page — not a bolt-on experiment.",
    tags: ["Agentic AI", "React", "Node.js", ".NET"],
  },
  {
    num: "02 / LEARNING",
    title: "Machine Learning Solutions",
    copy: "Models trained and served with Python, integrated directly into the backend and data layers your product already runs on.",
    tags: ["Python", "Machine Learning", "Data Intelligence"],
  },
  {
    num: "03 / INTEGRATION",
    title: "Smart APIs & Automation",
    copy: "AI capabilities exposed through the same microservices and DevOps pipelines used for everything else we ship — no separate stack to maintain.",
    tags: ["Microservices", "DevOps", "API Integration"],
  },
];

const FAQS = [
  {
    q: "Why these specific technologies?",
    a: "We pick frameworks that fit the project's constraints — team size, timeline, scale — not whatever's trending. Every technology on this page is one we've shipped production work with.",
  },
  {
    q: "Do you work with a stack we already have in place?",
    a: "Yes — most engagements start by working within your existing stack. What's listed here is what we default to on new builds, not a requirement to switch.",
  },
  {
    q: "Do you offer AI integration alongside these technologies?",
    a: "Yes — every stack on this page can plug into our AI services (agentic AI, generative AI, recommendation engines) when you're ready. See our AI Solutions page for the five dedicated services.",
  },
  {
    q: "Can you support legacy or less common stacks?",
    a: "In most cases, yes — legacy system modernization is one of our core services. Tell us what you're running and we'll figure out the right approach.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the facts panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag className={className} custom={i} variants={fadeUp} initial={reduceMotion ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.2 }} {...rest}>
      {children}
    </MotionTag>
  );
}

const Technologies = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    // pageDescriptions.js holds the title and description this route actually
    // ships with — useSocialMeta overrides both from there on every render.
    // These two are kept in step with it rather than removed: resolveDescription
    // only rewrites a description tag that already exists, so dropping the
    // element here would leave the page with no description at all.
    document.title = "Our Tech Stack: 38+ Tools Across React, .NET & AI | InQmise";

    const description = document.createElement("meta");
    description.name = "description";
    description.content = "The 38+ frameworks and tools we build on, across six categories: React, Next.js, .NET Core, Node, Flutter, AWS, Azure and our AI stack. Chosen per project.";
    document.head.appendChild(description);

    const keywords = document.createElement("meta");
    keywords.name = "keywords";
    keywords.content = "React JS development company Delhi, Next.js development services Delhi NCR, Angular development company Delhi, Vue.js development services Delhi, MERN stack development company Delhi, Node.js development company Noida, Python development services Delhi NCR, full stack development company Delhi, DevOps services company Delhi, microservices development Delhi, AWS cloud development services Delhi, cloud native application development Delhi NCR, Flutter app development company Delhi, AI development company delhi NCR, machine learning development, agentic AI development, agent AI services, modern tech stack development, agentic AI development company, custom AI solutions company, AI software development company, generative AI development services, recommendation engine development company, AI integration services, custom software development company, enterprise AI solutions provider, RAG application development services, AI development company in India";
    document.head.appendChild(keywords);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/technologies");

    // The company entity (@id #organization) is declared once, in index.html.
    // This page used to restate it with its own url, description and
    // areaServed; every copy sharing that @id is merged by Google as the same
    // entity, so the conflicting copies made its details unpredictable.

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a } })),
    });
    document.head.appendChild(faqScript);
  }, []);

  useEffect(() => {
    const sectionKey = location.hash.slice(1);
    if (sectionKey) {
      const sectionElement = document.getElementById(sectionKey);
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname": return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject": return value.trim() ? "" : "Subject is required.";
      case "description": return value.trim() ? "" : "Tell us a little about your project.";
      default: return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }
    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => setContactStatus("error"));
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">Modern tech stack · Delhi NCR</p>
          <h1>
            React, .NET and AI: the stack behind<br /><em>everything we ship.</em>
          </h1>
          <p className="ea-lede">
            React, .NET, Node.js, Flutter and more: six categories, {totalTools}+ frameworks and tools — chosen for the project, not the résumé. Here's exactly what powers our web, mobile, backend, and AI work.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">Start your project ↗</button>
            <a className="ea-text-link" href="/services" onClick={(e) => { e.preventDefault(); navigate("/services"); }}>See our services</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / TECH STACK <b>LIVE</b></div>
          {QUICK_FACTS.map((fact) => (
            <div className="cd-facts-row" key={fact.label}>
              <strong>{fact.value}</strong>
              <span>{fact.label}</span>
            </div>
          ))}
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>BUSINESSES THAT TRUSTED US WITH REAL LAUNCHES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* TECH CATEGORIES */}
      <section className="ea-wrap ea-section" id="stack">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">The full stack</div>
            <h2>Six categories,<br />one accountable team.</h2>
          </div>
          <p>Every category below is a real, in-house capability — not a subcontracted skill.</p>
        </Reveal>
        {tech.map((cat, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={cat.key} id={cat.key}>
            <div className="ea-num">{CATEGORY_NUM_LABELS[cat.key] || String(i + 1).padStart(2, "0")}</div>
            <h3>{cat.name}</h3>
            <p>{cat.Descriptions2}</p>
            <div className="ea-tags">{cat.children.map((toolItem) => <span key={toolItem.name}>{toolItem.name}</span>)}</div>
          </Reveal>
        ))}
      </section>

      {/* AI & AUTOMATION */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">AI & automation</div>
              <h2>The same stack,<br />built to think.</h2>
            </div>
            <p>AI isn't a separate practice here — it's layered onto the same technologies listed above.</p>
          </Reveal>
          {AI_CAPABILITIES.map((item, i) => (
            <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
              <div className="ea-num">{item.num}</div>
              <h3>{item.title}</h3>
              <p>{item.copy}</p>
              <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            </Reveal>
          ))}
          <div style={{ marginTop: 28 }}>
            <a className="ea-text-link" href="/ai-solutions" onClick={(e) => { e.preventDefault(); navigate("/ai-solutions"); }}>Explore all AI solutions →</a>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="our-process" className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Our process</div>
            <h2>How we choose<br />the right stack.</h2>
          </div>
          <p>The same seven-stage process, applied to every technology decision we make.</p>
        </Reveal>
        <div className="aa-process-line">
          {workflowsteps.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="aa-process-row" key={step.number}>
              <div className="aa-process-copy"><h3>{step.title}</h3><p>{step.description}</p></div>
              <div className="aa-process-node">{step.number}</div>
              <div />
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Why choose us</div>
              <h2>What sets our<br />team apart.</h2>
            </div>
            <p>The same standards behind every technology decision we make.</p>
          </Reveal>
          <div className="ea-cards">
            {chooseus.map((item, i) => (
              <Reveal as="article" i={(i % 3) + 1} reduceMotion={reduceMotion} className="ea-card" key={item.title}>
                <div className="ea-icon"><item.icon aria-hidden="true" /></div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>React, .NET and technology stack FAQs</h2>
            <p>Short answers to the questions we hear most about our stack.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `tech-faq-panel-${i}`;
            const buttonId = `tech-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenFaq(isOpen ? -1 : i)}>
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="tech-contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div>
            <h2 id="tech-contact-heading">Ready to build with the right stack?</h2>
            <p>Tell us what you're building and we'll help you figure out the right technologies for it.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Start your project.</h3>
            <p>Tell us what you're building and we'll get back to you within one business day.</p>

            <form onSubmit={handleContactSubmit} aria-label="Technologies page project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="tech-fullname">Full Name</label>
                <input
                  id="tech-fullname" name="fullname" type="text" value={formData.fullname} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)} aria-describedby={contactErrors.fullname ? "tech-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="tech-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="tech-phone">Phone Number (optional)</label>
                  <input
                    id="tech-phone" name="phone" type="tel" value={formData.phone} onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)} aria-describedby={contactErrors.phone ? "tech-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="tech-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="tech-email">Email</label>
                  <input
                    id="tech-email" name="email" type="email" value={formData.email} onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)} aria-describedby={contactErrors.email ? "tech-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="tech-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="tech-subject">Subject</label>
                <input
                  id="tech-subject" name="subject" type="text" placeholder="e.g., Which stack fits a new AI-powered platform"
                  value={formData.subject} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)} aria-describedby={contactErrors.subject ? "tech-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="tech-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="tech-description">Project Details</label>
                <textarea
                  id="tech-description" name="description" rows={3} value={formData.description} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)} aria-describedby={contactErrors.description ? "tech-description-error" : undefined}
                />
                {contactErrors.description && <p id="tech-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Technologies;
